import { ProForADayConfirmation } from "MVVM/Components/Modals/ProForADayConfirmation";
import { usePortal } from "MVVM/Hooks/usePortal";
import { useUser } from "MVVM/Hooks/useUser";
import styled from "styled-components";
import { useState } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import { useRecoilState } from "recoil";

import theme from "theme/themeThree";
import { AdRemovalCTAViewModel } from "../ViewModel";
import { CreatorCreditIcon } from "MVVM/Views/Pricing/Components/Table/CreatorCreditIcon";
import { analytics } from "MVVM/Analytics";
import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";

export const ProForADayButton = ({ location }: { location: string }) => {
  const [proForADayModalOpen, setProForADayModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setAllstarModalState] = useRecoilState(AllstarModalState);
  const { subscriptionCreate } = AdRemovalCTAViewModel();
  const { allstarUser } = useUser();
  const userGroup = allstarUser?.user?.userGroup?.type;
  const hasPremiumAccess = ["PRO", "CREATOR_PLAT"].includes(userGroup);
  const { Portal: ProForADayPortal } = usePortal(
    <ProForADayConfirmation
      isOpen={proForADayModalOpen}
      setIsOpen={setProForADayModalOpen}
    />,
    "root",
  );
  const handleProForADay = async () => {
    if (hasPremiumAccess) return;

    analytics.track("Button - Click", {
      locationIdentifier: "pro_for_a_day_cta_attempt",
      location: "remove_ads_banner",
    });

    setLoading(true);
    const result = await subscriptionCreate({ variables: { input: {} } });
    const errors = result?.data?.subscriptionCreate?.errors;
    if (errors?.length > 0) {
      analytics.track("Component Viewed", {
        locationIdentifier: "pro_for_a_day_insufficient_credits",
        location,
      });
      setAllstarModalState({
        isOpen: ModalType.MicroTransactions,
        data: { type: "pro_for_a_day" },
        functions: {},
      });
      setLoading(false);
      return;
    }

    analytics.track("Button - Click", {
      locationIdentifier: "pro_for_a_day_success",
      location,
    });
    setProForADayModalOpen(true);
    setLoading(false);
  };

  return (
    <>
      {proForADayModalOpen && ProForADayPortal}
      <Button onClick={handleProForADay} disabled={loading || hasPremiumAccess}>
        <Flex alignItems="center" justifyContent="center" style={{ gap: 6 }}>
          <Text variant="text.labelSmall" fontWeight={600} color="ice">
            {loading
              ? "Loading..."
              : hasPremiumAccess
              ? "Already Pro"
              : "Pro for a Day"}
          </Text>
          <Flex alignItems="center" style={{ gap: 4 }}>
            <CreatorCreditIcon size={14} addMargin={false} />
            <Text variant="text.labelSmall" fontWeight={600} color="ice">
              1
            </Text>
          </Flex>
        </Flex>
      </Button>
    </>
  );
};

const Button = styled(Box)<{ disabled?: boolean }>`
  background: linear-gradient(
    135deg,
    ${theme.colors.envy} 0%,
    ${theme.colors.premium} 100%
  );
  border-radius: 8px;
  color: ${theme.colors.ice};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  flex-shrink: 0;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  padding: 8px 16px;
  text-align: center;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: ${(props) => (props.disabled ? "none" : "translateY(-1px)")};
    box-shadow: ${(props) =>
      props.disabled ? "none" : "0 4px 12px rgba(0, 0, 0, 0.3)"};
  }

  &:active {
    transform: ${(props) => (props.disabled ? "none" : "translateY(0)")};
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    padding: 6px 12px;
  }
`;
